import { Module } from 'vuex';
import localStorageFacade, { StorageKey } from '@/shared/local-store-facade/LocalStoreFacade';

export interface StemdoerRate {
  stemdoerId: string;
  rate: number;
  projectId?: string;
}

export interface TeamRequest {
  name: string;
  initDate: Date;
  endDate: Date | null;
  jobDescription: string;
}

export interface CheckoutState {
  stemdoerRates: StemdoerRate[];
  selectedClients: string[];
  mutableTeamRequest: TeamRequest | {};
  createApprovedTeamRequestCheckBox: boolean;
}

export const checkoutStore: Module<CheckoutState, any> = {
  namespaced: true,
  state: () => ({
    stemdoerRates: [],
    selectedClients: [],
    mutableTeamRequest: {},
    createApprovedTeamRequestCheckBox: false
  }),
  mutations: {
    SET_STEMDOER_RATES(state, { rates, companyId }) {
      state.stemdoerRates = rates;
      localStorageFacade.set(StorageKey.CHECKOUT_STORE, {
        rates,
        selectedClients: state.selectedClients,
        mutableTeamRequest: state.mutableTeamRequest,
        createApprovedTeamRequestCheckBox: state.createApprovedTeamRequestCheckBox,
        companyId
      });
    },
    UPDATE_STEMDOER_RATE(state, { stemdoerId, rate, companyId }) {
      const idx = state.stemdoerRates.findIndex(r => r.stemdoerId === stemdoerId);
      if (idx !== -1) {
        state.stemdoerRates[idx].rate = rate;
      } else {
        state.stemdoerRates.push({ stemdoerId, rate });
      }
      localStorageFacade.set(StorageKey.CHECKOUT_STORE, {
        rates: state.stemdoerRates,
        selectedClients: state.selectedClients,
        mutableTeamRequest: state.mutableTeamRequest,
        createApprovedTeamRequestCheckBox: state.createApprovedTeamRequestCheckBox,
        companyId
      });
    },
    UPDATE_STEMDOER_PROJECT(state, { stemdoerId, project, companyId }) {
      // Si project es un objeto, extrae solo el id
      const idToSet = project && typeof project === 'object' && 'id' in project ? project.id : project || null;
      const idx = state.stemdoerRates.findIndex(r => r.stemdoerId === stemdoerId);
      if (idx !== -1) {
        state.stemdoerRates[idx].projectId = idToSet;
      } else {
        const existingRate = state.stemdoerRates.find(r => r.stemdoerId === stemdoerId)?.rate ?? null;
        state.stemdoerRates.push({
          stemdoerId,
          rate: existingRate,
          projectId: idToSet
        });
      }
      localStorageFacade.set(StorageKey.CHECKOUT_STORE, {
        rates: state.stemdoerRates,
        selectedClients: state.selectedClients,
        mutableTeamRequest: state.mutableTeamRequest,
        createApprovedTeamRequestCheckBox: state.createApprovedTeamRequestCheckBox,
        companyId
      });
    },
    SET_SELECTED_CLIENTS(state, { selectedClients, companyId }) {
      state.selectedClients = selectedClients;
      localStorageFacade.set(StorageKey.CHECKOUT_STORE, {
        rates: state.stemdoerRates,
        selectedClients,
        mutableTeamRequest: state.mutableTeamRequest,
        createApprovedTeamRequestCheckBox: state.createApprovedTeamRequestCheckBox,
        companyId
      });
    },
    SET_MUTABLE_TEAM_REQUEST(state, { mutableTeamRequest, companyId }) {
      const hasValue = mutableTeamRequest && Object.values(mutableTeamRequest).some(v => v !== null && v !== undefined && v !== '');
      if (hasValue) {
        state.mutableTeamRequest = mutableTeamRequest;
        localStorageFacade.set(StorageKey.CHECKOUT_STORE, {
          rates: state.stemdoerRates,
          selectedClients: state.selectedClients,
          mutableTeamRequest,
          createApprovedTeamRequestCheckBox: state.createApprovedTeamRequestCheckBox,
          companyId
        });
      }
    },
    SET_CREATE_APPROVED_TEAM_REQUEST_CHECKBOX(state, { value, companyId }) {
      state.createApprovedTeamRequestCheckBox = value;
      localStorageFacade.set(StorageKey.CHECKOUT_STORE, {
        rates: state.stemdoerRates,
        selectedClients: state.selectedClients,
        mutableTeamRequest: state.mutableTeamRequest,
        createApprovedTeamRequestCheckBox: value,
        companyId
      });
    },
    CLEAR_CHECKOUT(state) {
      state.stemdoerRates = [];
      state.selectedClients = [];
      state.mutableTeamRequest = {};
      state.createApprovedTeamRequestCheckBox = false;
      localStorageFacade.remove(StorageKey.CHECKOUT_STORE);
    }
  },
  actions: {
    initStemdoerRatesFromCart({ commit, rootState, state }) {
      const companyId = rootState.cartStore.company?.id;
      const savedRaw = localStorageFacade.get(StorageKey.CHECKOUT_STORE);
      let saved = null;
      if (typeof savedRaw === 'string') {
        try {
          saved = JSON.parse(savedRaw);
        } catch {
          saved = null;
        }
      } else {
        saved = savedRaw;
      }
      if (saved && saved.companyId === companyId) {
        commit('SET_STEMDOER_RATES', { rates: saved.rates, companyId });
        commit('SET_SELECTED_CLIENTS', { selectedClients: saved.selectedClients || [], companyId });
        if (saved.mutableTeamRequest && Object.keys(saved.mutableTeamRequest).length > 0) {
          commit('SET_MUTABLE_TEAM_REQUEST', { mutableTeamRequest: saved.mutableTeamRequest, companyId });
        } else if (state.mutableTeamRequest && Object.keys(state.mutableTeamRequest).length > 0) {
          // Si ya hay uno en memoria, no lo sobrescribas
          commit('SET_MUTABLE_TEAM_REQUEST', { mutableTeamRequest: state.mutableTeamRequest, companyId });
        }
        return;
      }
      const rates =
        rootState.cartStore.teamRequest?.stemdoerRates?.map(sr => ({
          stemdoerId: sr.stemdoer.id,
          rate: sr.rate
        })) || [];
      commit('SET_STEMDOER_RATES', { rates, companyId });
      commit('SET_SELECTED_CLIENTS', { selectedClients: [], companyId });
    },
    updateStemdoerRate({ commit, rootState }, payload) {
      const companyId = rootState.cartStore.company?.id;
      commit('UPDATE_STEMDOER_RATE', { ...payload, companyId });
    },
    updateProject({ commit, rootState }, payload) {
      const companyId = rootState.cartStore.company?.id;
      commit('UPDATE_STEMDOER_PROJECT', { ...payload, companyId });
    },
    updateSelectedClients({ commit, rootState }, selectedClients) {
      const companyId = rootState.cartStore.company?.id;
      commit('SET_SELECTED_CLIENTS', { selectedClients, companyId });
    },
    updateMutableTeamRequest({ commit, rootState }, mutableTeamRequest) {
      const companyId = rootState.cartStore.company?.id;
      commit('SET_MUTABLE_TEAM_REQUEST', { mutableTeamRequest, companyId });
    },
    updateCreateApprovedTeamRequestCheckBox({ commit, rootState }, value) {
      const companyId = rootState.cartStore.company?.id;
      commit('SET_CREATE_APPROVED_TEAM_REQUEST_CHECKBOX', { value, companyId });
    },
    clearCheckout({ commit }) {
      commit('CLEAR_CHECKOUT');
    }
  }
};
